<template>
  <v-card
    ref="uploadCardRef"
    class="upload-card card-action"
    elevation="0"
    :data-popular-text="showFreeBadge ? freeBadgeText : null"
    @click="handleCardClick"
  >
    <v-card-text>
      <div v-if="!showFreeBadge && !isUploading" class="model-selector mb-4">
        <v-slider
          v-model="selectedModel"
          :ticks="modelTicks"
          :labels="modelLabels"
          :max="1"
          :min="0"
          :step="1"
          :disabled="isUploading"
          show-ticks="always"
          density="comfortable"
          color="primary"
          track-color="grey-lighten-1"
        >
          <template v-slot:prepend>
            <v-icon color="primary">mdi-lightning-bolt</v-icon>
          </template>
          <template v-slot:append>
            <v-icon color="primary">mdi-star</v-icon>
          </template>
        </v-slider>
        <!-- Per-tier credit-cost row: matches the slider's "Schnell" /
             "Premium" tick positions so the user sees at a glance that
             Premium costs more. Stays visible regardless of which tier
             is selected. -->
        <div class="model-cost-row" data-testid="upload-tier-cost-row">
          <span
            class="model-cost-pill model-cost-pill--fast"
            :class="{ 'model-cost-pill--active': selectedModel === 0 }"
          >
            <v-icon icon="mdi-circle-multiple" size="14" />
            {{ t('hero.upload.models.fast_cost_pill', { count: fastTierCredits }) }}
          </span>
          <span
            class="model-cost-pill model-cost-pill--quality"
            :class="{ 'model-cost-pill--active': selectedModel === 1 }"
          >
            <v-icon icon="mdi-circle-multiple" size="14" />
            {{ t('hero.upload.models.quality_cost_pill', { count: premiumTierCredits }) }}
          </span>
        </div>
        <div class="text-caption text-grey-darken-1 text-center">
          {{ selectedModelDescription }}
        </div>
        <!-- Selected-tier reinforcement: spelled out below the
             description so the user can't miss the cost before
             submitting. -->
        <div class="text-caption text-primary text-center mt-1" data-testid="upload-tier-cost-line">
          <!-- vue-i18n pluralization: count goes as the third positional arg
               so the "1 Credit | N Credits" branch resolves correctly. -->
          {{ t(
            'hero.upload.models.cost_line',
            { count: selectedModel === 0 ? fastTierCredits : premiumTierCredits },
            selectedModel === 0 ? fastTierCredits : premiumTierCredits,
          ) }}
        </div>
      </div>

      <div
        v-if="showProgressPanel"
        ref="progressPanelRef"
        class="upload-progress-panel card-action-subtle mb-4"
        data-testid="upload-progress-panel"
      >
        <div class="d-flex flex-column flex-sm-row justify-space-between align-start gap-3 mb-4">
          <div>
            <div class="text-overline text-primary mb-1">
              {{ t('hero.upload.progress.eyebrow') }}
            </div>
            <h3 class="text-h6 mb-2">
              {{ t('hero.upload.progress.title') }}
            </h3>
            <p class="text-body-2 text-medium-emphasis mb-0">
              {{ t('hero.upload.progress.description') }}
            </p>
          </div>
        </div>

        <div class="progress-metadata">
          <span class="text-caption text-medium-emphasis">
            {{ t('hero.upload.progress.currentFocus') }}
          </span>
          <strong class="text-body-2">{{ panelStatusText }}</strong>
        </div>

        <div
          v-if="progressValue !== null"
          class="progress-bar-row"
          data-testid="upload-progress-bar"
        >
          <v-progress-linear
            :model-value="progressValue"
            color="primary"
            bg-color="rgba(148, 163, 184, 0.24)"
            rounded
            height="8"
          />
          <span class="text-caption text-medium-emphasis progress-percent">
            {{ progressValue }}%
          </span>
        </div>

        <div class="progress-metadata">
          <span class="text-caption text-medium-emphasis">
            {{ t('hero.upload.progress.fileLabel') }}
          </span>
          <span class="text-body-2">
            {{ selectedFile?.name || t('hero.upload.progress.fileFallback') }}
          </span>
        </div>
      </div>
      
      <div
        v-if="!isUploading"
        class="upload-zone pa-8"
        :class="{ 'drag-over': isDragging }"
        :disabled="isUploading"
        @dragenter.prevent="isDragging = true"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <v-icon size="64" :color="isUploading ? 'primary' : 'grey-lighten-1'">
          {{ uploadIcon }}
        </v-icon>
        <v-progress-circular
            v-if="isUploading"
            :size="16"
            :width="2"
            color="primary"
            indeterminate
            class="ml-2"
        ></v-progress-circular>
        <div class="d-flex align-center justify-center mt-4">
          <span class="text-body-1">
            {{ uploadStatusText }}
          </span>          
        </div>
        <ul class="upload-helper-row">
          <li>
            <v-icon icon="mdi-image-outline" size="14" aria-hidden="true" />
            {{ t('hero.upload.helper.formats') }}
          </li>
          <li>
            <v-icon icon="mdi-timer-outline" size="14" aria-hidden="true" />
            {{ t('hero.upload.helper.time') }}
          </li>
        </ul>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden-input"
          @change="handleFileInput"
          :disabled="isUploading"
        >
      </div>
      
      <v-alert
        v-if="errorMessage"
        type="error"
        class="mt-4"
        variant="tonal"
      >
        {{ errorMessage }}
      </v-alert>
      
      <div v-if="selectedFile && !isUploading" class="selected-file mt-4">
        <v-chip
          closable
          @click:close="clearFile"
          color="primary"
          variant="outlined"
          class="file-chip"
        >
          {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
        </v-chip>
      </div>
      <UploadFeedbackModal
        v-model="showFeedbackModal"
        :filename="selectedFile?.name"
        @submit="handleFeedbackSubmit"
        @skip="handleFeedbackSkip"
      />
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { trackImageUpload, trackRateLimitError } from '@/utils/analytics';
import { useAuthStore } from '@/stores/auth';
// import { useRouter } from 'vue-router';
import { getIdToken } from 'firebase/auth';
import { useStore } from 'vuex';
import { loadPricingRules, getFallbackRules } from '@/utils/pricingRulesCache.mjs';
import UploadFeedbackModal from './UploadFeedbackModal.vue'
import {
  UPLOAD_PHASES,
  getUploadNarrativeStageKey,
  getUploadPhaseMeta,
  getUploadProgressValue,
} from '@/utils/uploadState.mjs';

const emit = defineEmits(['upload-success', 'upload-error', 'upload-state-change']);
const { t, te, locale } = useI18n();

const fileInput = ref(null);
const uploadCardRef = ref(null);
const progressPanelRef = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const isUploading = ref(false);
const errorMessage = ref('');
const uploadPhase = ref(UPLOAD_PHASES.IDLE);
const progressElapsedMs = ref(0);
const uploadPhaseStartedAt = ref(0);
let progressTimer = null;
const MIN_WAITING_PHASE_MS = 6500;

const authStore = useAuthStore();
// const router = useRouter();

const selectedModel = ref(0); // 0 = fast, 1 = high quality

const modelTicks = {
  0: t('hero.upload.models.fast'),
  1: t('hero.upload.models.quality')
};

const modelLabels = computed(() => ({
  0: t('hero.upload.models.fast_label'),
  1: t('hero.upload.models.quality_label')
}));

const selectedModelDescription = computed(() => {
  return selectedModel.value === 0
    ? t('hero.upload.models.fast_description')
    : t('hero.upload.models.quality_description');
});

// Per-tier credit cost. Sourced from the pricing-rules cache (which
// reads the Firestore `pricing_rules` catalog); falls back to the static
// defaults if the cache hasn't populated yet (e.g. first paint, no
// network). Defaults match `pricingRules.js` so the fallback never
// disagrees with what the backend will actually charge.
const tierPricingRules = ref(getFallbackRules('paid'));
loadPricingRules('paid')
  .then((rules) => { tierPricingRules.value = rules; })
  .catch((err) => { console.warn('Pricing rules load failed in UploadArea:', err?.message); });
const fastTierCredits = computed(
  () => Number.isFinite(tierPricingRules.value?.upload) ? tierPricingRules.value.upload : 1,
);
const premiumTierCredits = computed(
  () => Number.isFinite(tierPricingRules.value?.upload_premium) ? tierPricingRules.value.upload_premium : 2,
);
const uploadPhaseMeta = computed(() => getUploadPhaseMeta(uploadPhase.value));
const progressValue = computed(() => getUploadProgressValue(uploadPhase.value, progressElapsedMs.value));
const showProgressPanel = computed(() => isUploading.value && uploadPhaseMeta.value.showPanel);
const panelStatusText = computed(() => {
  const stageKey = getUploadNarrativeStageKey(uploadPhase.value, progressElapsedMs.value);
  if (!stageKey) {
    return '';
  }
  return t(stageKey);
});
const uploadIcon = computed(() => {
  if (!isUploading.value) {
    return 'mdi-cloud-upload';
  }

  switch (uploadPhase.value) {
    case UPLOAD_PHASES.BASE_REPORT_READY:
    case UPLOAD_PHASES.DONE:
      return 'mdi-check-circle';
    case UPLOAD_PHASES.ERROR:
      return 'mdi-alert-circle';
    case UPLOAD_PHASES.WAITING_FOR_BASE_REPORT:
      return 'mdi-file-document-cog';
    default:
      return 'mdi-cloud-upload';
  }
});

const uploadStatusText = computed(() => {
  if (!isUploading.value) {
    return t('hero.upload.dragDrop');
  }
  return t(uploadPhaseMeta.value.zoneKey);
});

const triggerFileInput = () => {
  fileInput.value.click();
};

// The whole card is the upload target — users intuitively click anywhere on
// it, not just the inner dashed zone, so guard against nested interactive
// children (slider, file chip, alert, feedback modal) and otherwise open the
// file picker.
const handleCardClick = (event) => {
  if (isUploading.value) return;
  if (event.target.closest('.model-selector, .file-chip, .v-alert, .v-dialog, .v-overlay')) return;
  triggerFileInput();
};

const clearProgressTimer = () => {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
};

const resetPhaseTiming = () => {
  uploadPhaseStartedAt.value = Date.now();
  progressElapsedMs.value = 0;
};

const startProgressTimer = () => {
  clearProgressTimer();
  progressTimer = setInterval(() => {
    progressElapsedMs.value = Date.now() - uploadPhaseStartedAt.value;
  }, 1000);
};

const setUploadPhase = (phase) => {
  uploadPhase.value = phase;

  if (phase === UPLOAD_PHASES.UPLOADING_FILE || phase === UPLOAD_PHASES.WAITING_FOR_BASE_REPORT) {
    resetPhaseTiming();
    startProgressTimer();
    return;
  }

  if (phase === UPLOAD_PHASES.ERROR || phase === UPLOAD_PHASES.BASE_REPORT_READY || phase === UPLOAD_PHASES.DONE || phase === UPLOAD_PHASES.IDLE) {
    clearProgressTimer();
  }
};

const handleFileInput = async (event) => {
  const file = event.target.files[0];
  if (file) {
    await validateAndSetFile(file);
    if (selectedFile.value) {
      handleUpload();
    }
  }
};

const handleDrop = async (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file) {
    await validateAndSetFile(file);
    if (selectedFile.value) {
      handleUpload();
    }
  }
};

const validateAndSetFile = (file) => {
  errorMessage.value = '';
  
  const validTypes = ['image/jpeg', 'image/png'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  if (!validTypes.includes(file.type)) {
    errorMessage.value = t('hero.upload.errors.fileType');
    return;
  }
  
  if (file.size > maxSize) {
    errorMessage.value = t('upload.errors.fileSize');
    return;
  }
  
  selectedFile.value = file;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const clearFile = () => {
  selectedFile.value = null;
  errorMessage.value = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Sits below the fixed v-app-bar (≈64px) with breathing room so the card's
// top edge — eyebrow, title, description — isn't clipped by the bar.
const UPLOAD_CARD_TOP_OFFSET = 96;

const scrollUploadCardIntoView = async () => {
  await nextTick();
  const cardElement = uploadCardRef.value?.$el ?? uploadCardRef.value;
  if (!cardElement || typeof cardElement.getBoundingClientRect !== 'function') {
    return;
  }
  const rect = cardElement.getBoundingClientRect();
  window.scrollTo({
    top: window.scrollY + rect.top - UPLOAD_CARD_TOP_OFFSET,
    behavior: 'auto',
  });
};

const finishUploadingState = () => {
  isUploading.value = false;
  emit('upload-state-change', false);
  clearProgressTimer();
};

const uploadFile = async () => {
  isUploading.value = true;
  setUploadPhase(UPLOAD_PHASES.UPLOADING_FILE);
  emit('upload-state-change', true);
  await scrollUploadCardIntoView();
  selectedModel.value = store.state.modelQuality === 'fast' ? 0 : 1;
  
  store.commit('setUserHadPremiumWhenUploaded', authStore.credits > 0);

  try {
    const formData = new FormData();
    formData.append('floorplan', selectedFile.value);
    formData.append('language', locale.value);
    formData.append('model', selectedModel.value === 0 ? 'fast' : 'quality');
    
    let headers = { 'Content-Type': 'multipart/form-data' };
    if (authStore.isAuthenticated) {
      const token = await getIdToken(authStore.user);
      headers.Authorization = `Bearer ${token}`;
    }

    setUploadPhase(UPLOAD_PHASES.WAITING_FOR_BASE_REPORT);
    const response = await axios.post('/api/upload', formData, { headers });

    if (!response.data.success) {
      throw new Error(response.data.error.message);
    }

    const remainingNarrativeTime = Math.max(
      0,
      MIN_WAITING_PHASE_MS - (Date.now() - uploadPhaseStartedAt.value),
    );
    if (remainingNarrativeTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingNarrativeTime));
    }

    setUploadPhase(UPLOAD_PHASES.BASE_REPORT_READY);
    finishUploadingState();
    emit('upload-success', response.data.data);
    
  } catch (error) {
    setUploadPhase(UPLOAD_PHASES.ERROR);
    // Handle specific HTTP status codes
    if (error.response?.status === 429) {
      const errorData = error.response.data;
      // Track rate limit error specifically
      trackRateLimitError(selectedFile.value.size, selectedFile.value.name);
      
      if (errorData?.isI18nKey) {
        errorMessage.value = t(errorData.message);
      } else {
        errorMessage.value = t('upload.errors.uploadLimit.message');
      }
    } 
    // Handle file upload errors
    else if (error.response?.status === 400) {
      if (error.response.data.error?.code === 'LIMIT_FILE_SIZE') {
        errorMessage.value = t('upload.errors.fileSize');
      } else if (error.response.data.error?.code === 'NO_FILE') {
        errorMessage.value = t('upload.errors.no_file');
      } else if (error.response.data.error?.code === 'NO_FLOORPLAN_DETECTED') {
        errorMessage.value = t('upload.errors.noFloorplan');
      } else {
        errorMessage.value = t('upload.errors.upload_error');
      }
    }
    // Handle AI provider errors
    else if (error.response?.status === 503) {
      errorMessage.value = t('upload.errors.rate_limit');
    }
    // Handle floorplan service errors
    else if (error.response?.data?.error?.code) {
      const errorCode = error.response.data.error.code.toLowerCase();
      // Only remap codes whose backend identifier doesn't match the i18n
      // key naming. Codes that already exist in upload.errors with a
      // proper translation (analysis_error, all_providers_failed,
      // processing_failed, invalid_image, parse_error, …) are looked up
      // directly so each can have its own user-friendly copy.
      const errorKeys = {
        'no_floorplan_detected': 'noFloorplan',
        'file_not_found': 'upload_error',
        'ai_provider_error': 'provider_error',
        'invalid_provider': 'provider_error'
      };

      const translationKey = errorKeys[errorCode] || errorCode;
      const i18nPath = `upload.errors.${translationKey}`;
      // vue-i18n's composable te() only checks the active locale's
      // messages — it does not walk the fallbackLocale chain. So we also
      // probe the EN fallback before deciding the key is missing; that
      // way a key added only to en.json still renders via t()'s normal
      // fallback machinery. Backend message comes last because it can be
      // hardcoded English from the AI provider layer or hardcoded German
      // from the route layer — preferring our own translated copy keeps
      // the user on the active locale.
      if (te(i18nPath) || te(i18nPath, 'en')) {
        errorMessage.value = t(i18nPath);
      } else {
        if (error.response.data.error.message) {
          // Surface to ops; never to the user.
          console.error('[upload] unmapped backend error code', errorCode, error.response.data.error.message);
        }
        errorMessage.value = t('upload.errors.upload_error');
      }
    }
    // Handle other errors
    else {
      errorMessage.value = t('upload.errors.networkError');
    }
    
    emit('upload-error', errorMessage.value);
  } finally {
    if (isUploading.value) {
      finishUploadingState();
    }
    setUploadPhase(errorMessage.value ? UPLOAD_PHASES.ERROR : UPLOAD_PHASES.DONE);
    setUploadPhase(UPLOAD_PHASES.IDLE);
  }
};

const showFeedbackModal = ref(false)
const feedbackSubmitted = ref(false)

const handleUpload = async () => {
  // Track upload attempt before starting upload
  trackImageUpload(selectedFile.value.size, 'attempt', selectedFile.value.name)
  
  store.commit('resetAnalysisData');  
  
  // Show feedback modal before starting upload
  if (!feedbackSubmitted.value) {
    setUploadPhase(UPLOAD_PHASES.COLLECTING_FEEDBACK)
    showFeedbackModal.value = true
    return
  }
  
  await uploadFile()
}

// Modify handleFeedbackSubmit function
const handleFeedbackSubmit = async (feedback) => {
  store.commit('setUserIntent', feedback)
  
  showFeedbackModal.value = false
  feedbackSubmitted.value = true
  await uploadFile()
}

const handleFeedbackSkip = async () => {
  showFeedbackModal.value = false
  feedbackSubmitted.value = true
  // Continue with upload
  await uploadFile()
}

const showFreeBadge = computed(() => {
  return (!authStore.isAuthenticated || authStore.credits <= 0) && !isUploading.value;
});

// Authenticated users with 0 credits previously saw the same "Jetzt
// kostenlos testen" badge as anonymous visitors, which is confusing — they
// already started, and the trial is one-per-IP. Show a clearer label that
// signals "you have one free analysis available" instead.
const freeBadgeText = computed(() =>
  authStore.isAuthenticated
    ? t('hero.upload.free_trial_badge_authed')
    : t('hero.upload.free_trial_badge'),
);

const store = useStore();

watch(selectedModel, (newValue) => {
  store.commit('setModelQuality', newValue === 0 ? 'fast' : 'quality');
});

// uploadFile() already scrolls the whole card into view with an offset for
// the fixed app bar; re-scrolling the inner panel here would pull the card's
// header (eyebrow/title) up behind the bar.
watch(showProgressPanel, async (visible) => {
  if (!visible) {
    return;
  }
  await scrollUploadCardIntoView();
});

// Initialize from store
onMounted(() => {
  selectedModel.value = store.state.modelQuality === 'fast' ? 0 : 1;
});

onBeforeUnmount(() => {
  clearProgressTimer();
});
</script>

<style scoped>
.upload-card {
  border: 2px dashed var(--border-color);
  transition: all 0.3s ease;
  animation: glowingBorder 2s ease-in-out infinite;
  box-shadow: 0 0 20px 0 var(--primary);
  position: relative;
  cursor: pointer;
}

@keyframes glowingBorder {
  0% {
    box-shadow: 0 0 0 0 var(--primary);
    border-color: var(--border-color);
  }
  50% {
    box-shadow: 0 0 15px 0 var(--primary);
    border-color: var(--primary);
  }
  100% {
    box-shadow: 0 0 0 0 var(--primary);
    border-color: var(--border-color);
  }
}

.upload-zone {
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  border-radius: 12px;
  background: var(--border-color);
}

.upload-helper-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding: 0;
  list-style: none;
  color: var(--text-secondary);
  font-size: var(--font-size-caption);
}

.upload-helper-row li {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.54);
}

.upload-progress-panel {
  padding: 20px;
}

.progress-metadata {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.progress-bar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0 8px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.progress-bar-row :deep(.v-progress-linear) {
  flex: 1;
}

.progress-percent {
  min-width: 42px;
  text-align: right;
}

.upload-zone:hover {
  background: var(--background);
}

.drag-over {
  background: var(--background);
  border-color: var(--primary);
}

.upload-zone .v-icon {
  transition: all 0.3s ease;
}

.upload-zone:hover .v-icon {
  color: var(--primary) !important;
  transform: translateY(-5px);
}

.hidden-input {
  display: none;
}

.file-chip {
  background: white !important;
  border-color: var(--primary) !important;
  color: var(--primary) !important;
  font-weight: var(--font-weight-medium);
}

:deep(.v-alert) {
  border-radius: 12px;
}

:deep(.v-alert.v-alert--variant-tonal) {
  background: #fee2e2;
  color: #991b1b;
}

.upload-card:hover {
  border-color: var(--primary);
  animation: none;
  box-shadow: 0 0 20px 0 var(--primary);
}

.upload-card::before {
  content: attr(data-popular-text);
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--primary);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: var(--font-size-body-2);
  font-weight: var(--font-weight-medium);
  z-index: 2;
  display: none;
}

.upload-card[data-popular-text]::before {
  display: block;
}

.model-selector {
  padding: 16px 16px 0 16px;
  margin-top: 24px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Per-tier cost pills sit directly under the slider; their horizontal
   layout mirrors the slider's two tick positions (Schnell ← → Premium)
   so the user reads "this tier costs X" at the same glance. */
.model-cost-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0 4px 8px 4px;
}

.model-cost-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: var(--font-size-caption, 0.75rem);
  font-weight: var(--font-weight-medium, 500);
  background: rgba(148, 163, 184, 0.16);
  color: #475569;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  /* Without this, the German "2 Credits" wraps inside the pill on narrow
     mobiles (iPhone SE / 375px), producing a stacked "2 / Credits" pill
     that breaks the row's symmetry. */
  white-space: nowrap;
}

.model-cost-pill--active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* On very narrow phones (≤380px) the pills bump into each other once
   `nowrap` kicks in; tighten padding + gap so both still fit comfortably
   with the slider above. */
@media (max-width: 380px) {
  .model-cost-row {
    gap: 8px;
    padding: 0 0 8px 0;
  }
  .model-cost-pill {
    padding: 3px 8px;
  }
}

:deep(.v-slider .v-slider-track__fill) {
  background-color: var(--primary);
}

:deep(.v-slider .v-slider-thumb__surface) {
  border-color: var(--primary);
}

:deep(.v-slider .v-slider-thumb__surface::before) {
  background-color: var(--primary);
}

@media (max-width: 600px) {
  .upload-progress-panel {
    padding: 16px;
  }

  .progress-metadata {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
